<template>

  <div class="product-detail-dialog" v-if="isOpen">
    <div class="product-detail-dialog-container" ref="container">
      <div class="close-button">
        <div v-on:click="closeDialog">
          <img src="@/assets/img/xmark.svg"/>
        </div>
      </div>
      <div class="product-detail-container">
        <div class="left-infos">
          <span class="name">{{ product.name }}</span>
          <span class="infos" v-if="product.info" v-html="product.info"></span>

          <div class="stand-container" v-if="product.stands">
            <i class="fas fa-map-marker-alt"></i>
            <div class="stands">
              <div class="stand" v-for="(stand,index) of product.stands" v-on:click="callOpenStandDialog(stand,$event)"
                   v-html="(stand.getDisplayName() + (index !== product.stands.length -1 ? ',' : ''))">
              </div>
            </div>
          </div>
        </div>
        <div class="product-logo" v-if="product.logo">
          <img :src="product.getLogoUrl()">
        </div>
      </div>
      <div class="image-gallery" v-if="product.images && product.images.length > 0">
        <div class="product-image" v-for="image of product.images" v-on:click="openImageModal(image)">
          <img v-bind:alt="image.text" v-bind:src="image.getUrl()">

        </div>
      </div>
      <div class="footer">
        <template v-if="product.themesDisplay">
          <theme-root v-for="three of themeTrees" v-bind:root="three.root" v-bind:themes="themes" v-bind:entityThemes="product.themesDisplay"></theme-root>
        </template>
      </div>
      <div class="image-modal" v-bind:class="isImageModalOpen ? 'image-model-open':'image-modal-closed'">
        <div class="image-modal-container">
          <div class="close-button">
            <div v-on:click="closeImageModal">
              <img src="@/assets/img/xmark.svg"/>
            </div>
          </div>
          <img class="product-image" ref="modalImage">
        </div>
      </div>
    </div>
  </div>

</template>

<script lang="ts">
import {Options, Vue} from "vue-class-component";
import {Product} from "@/entities/Product";
import {isDefined} from "@/utils";
import Stand from "@/entities/Stand";
import {Theme} from '@/entities/Theme';
import {ThemeTree, ThemeTreeNode} from '@/entities/ThemeTree';
import ThemeRoot from '@/components/ThemeRoot.vue';
import LazyLoadContainer from '@/components/LazyLoadComponent.vue';
import ExhibitorDetailDialog from '@/components/ExhibitorDetailDialog.vue';
import {Image} from '@/entities/Image';


@Options({
  components: {ThemeRoot, LazyLoadContainer},
  props: {
    themes: {
      default: [],
      required: true,
      type: Array
    },
    openStandDialog: {
      required: true,
      type: Function
    },
    onCloseDialog:{
      required: true,
      type: Function
    }
  }
})
export default class ProductDetailDialog extends Vue {

  private product: Product;
  public isOpen: boolean = false;
  private openStandDialog: Function;
  private themeTrees: ThemeTree[];
  private themes: Theme[];
  private onCloseDialog: Function;
  private isImageModalOpen: boolean = false;

  constructor(props) {
    super(props);
  }

  public closeImageModal(): void {
    this.isImageModalOpen = false;
  }

  public openImageModal(image: Image): void {
    (this.$refs.modalImage as HTMLImageElement).src = image.getFullImageUrl();
    this.isImageModalOpen = true;
  }

  public openDialog(product: Product): void {
    this.product = product;
    this.buildThemeTreeForProduct();
    this.addClickListener();
    this.isOpen = true;
  }

  private buildThemeTreeForProduct() {
    const roots: Theme[] = this.themes.filter(t => t.parentId === '-1' || t.parentId === 'ROOT');
    Theme.sort(roots);
    const themeTrees: ThemeTree[] = [];
    for (const root of roots) {
      const rootChildren = this.themes.filter(t => t.parentId === root.id);
      Theme.sort(rootChildren);
      const themeTree = new ThemeTree(new ThemeTreeNode(root, rootChildren.map(t => new ThemeTreeNode(t, []))));
      this.traverseTree(themeTree.root);
      themeTrees.push(themeTree);
    }
    this.themeTrees = themeTrees;
  }

  public traverseTree(root: ThemeTreeNode) {
    for (const node of root.children) {
      const children = this.themes.filter(t => t.parentId === node.theme.id);
      if (children.length > 0) {
        node.children = children.map(c => new ThemeTreeNode(c, []));
        this.traverseTree(node);
        for (const n of node.children) {
          this.traverseTree(n);
        }
      }
    }
  }

  public closeDialog(event: Event): void {
    this.removeClickListener();
    this.isOpen = false;
    this.onCloseDialog(event);
  }

  private callOpenStandDialog(stand: Stand, event: Event): void {
    this.removeClickListener();
    this.openStandDialog(stand, event)
  }

  private onClick(event) {
    if (isDefined(this.$refs.container) && this.isOpen) {
      const container = this.$refs.container as any;
      if (isDefined(container)) {
        if (!container.contains(event.target)) {
          this.closeDialog(event);
        }
      }
    }
  }

  public addClickListener() {
    window.addEventListener('click', this.onClick);
  }

  private removeClickListener() {
    window.removeEventListener('click', this.onClick);
  }


}
</script>

<style scoped lang="scss">

.product-detail-dialog {
  position: fixed;
  z-index: 9997;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transition: opacity .3s ease;

  &-container {
    position: relative;
    padding: 64px;
    width: 100%;
    max-width: 1000px;
    max-height: 80vh;
    overflow: auto;
    -webkit-overflow-scrolling: touch;
    margin: 0 auto;
    transition: all .3s ease;
    background: #fff;
    box-shadow: 0 0 100px rgb(0 0 0 / 30%);

    .close-button {
      position: absolute;
      top: 0;
      right: 50px;

      > div {
        width: 50px;
        height: 50px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        color: white;
        background-color: var(--primary-color);
        font-size: 1.5rem;
        cursor: pointer;
        position: fixed;

        > img {
          width: 25px;
        }
      }
    }

    .product-detail-container {
      display: flex;
      flex-direction: row;
      justify-content: space-between;

      .left-infos {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        text-align: left;
        max-width: 75%;


        .name {
          font-size: 2rem;
          line-height: 1.2;
          font-weight: bold;
          text-transform: uppercase;
          margin-bottom: 16px;
        }

        .exhibitor-button {
          display: flex;
          flex-direction: row;
          cursor: pointer;
          font-weight: bold;
          align-items: center;
          justify-content: center;
          color: white;
          background-color: var(--primary-color);
          padding: 12px;
          margin-top: 12px;
        }

        .plz, .country, .phone, .web, .email, .address, .stand {
          font-size: 1rem;
          text-align: left;
          line-height: 1.5;
          white-space: nowrap;
          overflow: hidden;
          word-break: keep-all;
          text-overflow: ellipsis;
          max-width: 100%;
          display: flex;
          align-items: center;
          margin: 5px 0;

          > i {
            color: var(--primary-color);
            margin-right: 12px;
            font-size: 1.6rem;
          }
        }

        .stand {
          cursor: pointer;
        }

        .country {
          margin-bottom: 24px;
        }

        .web, .email {
          > a {
            color: #000000;
          }
        }

        .stand-container {
          margin-top: 24px;
          display: flex;
          flex-direction: row;
          align-items: center;

          > i {
            margin-right: 12px;
            font-size: 1.8rem;
            color: var(--primary-color);
          }

          .stands {
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;
            align-items: center;

            .stand {
              font-weight: bold;
              white-space: nowrap;
              word-break: keep-all;
              margin: 0 3px;
            }
          }
        }
      }

      .product-logo, .product-logo-letter {
        display: flex;
        justify-content: center;
        width: 20vw;
        height: 20vw;
        max-width: 200px;
        max-height: 200px;
        border: 1px solid rgba(128, 128, 128, 0.5);

        > img {
          max-height: 80%;
          width: 80%;
          max-width: 80%;
          align-self: center;
        }
      }

      .product-logo-letter {
        display: flex;
        justify-content: center;
        align-items: center;
        background-color: #efefef;

        span {
          font-size: 3rem;
          font-weight: bold;
          color: #D3D3D3;
        }
      }

    }

    .product-infos {
      text-align: left;
      font-size: 1rem;
      line-height: 1.33;
      margin-top: 32px;
    }


    .product-logo {
      width: 8vw;
      border: 1px solid rgba(128, 128, 128, 0.5);
      margin-right: 32px;
      height: 8vw;
      display: flex;
      justify-content: center;

      .lazy-loading-component {
        display: flex;
        width: 100%;
        justify-content: center;
      }
    }

    .product-logo {
      img {
        max-height: 80%;
        width: 80%;
        max-width: 80%;
        align-self: center;
      }
    }


    .image-gallery {
      margin: 12px 0;
      display: flex;
      max-width: 100%;
      flex-direction: row;
      flex-wrap: wrap;

      .product-image {
        width: 10vw;
        border: 1px solid rgba(128, 128, 128, 0.5);
        margin-right: 32px;
        height: 10vw;
        display: flex;
        justify-content: center;
        margin-bottom: 32px;
      }

      .product-image {
        img {
          max-height: 80%;
          width: 80%;
          max-width: 80%;
          align-self: center;
        }
      }
    }

    .footer {
      margin-top: 16px;
      display: flex;
      justify-content: flex-start;
      flex-wrap: wrap;
      flex-direction: column;

      > ul {
        margin: 16px 0 0 0;
        padding-left: 15px;

        .theme {
          text-align: left;
          font-weight: bold;
          font-size: 1rem;
        }
      }
    }

    .image-modal {
      position: fixed;
      z-index: 9997;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.8);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      transition: opacity .3s ease;

      &-container {
        position: relative;
        padding: 64px;
        width: 100%;
        max-width: 1000px;
        max-height: 80vh;
        overflow: auto;
        -webkit-overflow-scrolling: touch;
        margin: 0 auto;
        transition: all .3s ease;
        background: #fff;
        box-shadow: 0 0 100px rgb(0 0 0 / 30%);
      }

      .close-button {
        position: absolute;
        top: 0;
        right: 50px;

        > div {
          width: 50px;
          height: 50px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: white;
          background-color: var(--primary-color);
          font-size: 1.5rem;
          cursor: pointer;
          position: fixed;

          > img {
            width: 25px;
          }
        }
      }

      .product-image {
        align-self: center;
      }
    }

    .image-modal-open {
      display: block;
    }

    .image-modal-closed {
      display: none;
    }

  }


  @media only screen and (max-width: 766px) {
    &-container {
      padding: 64px 16px 16px 16px;
    }
  }

}

</style>
